import {
  DatePicker,
  DatePickerProps,
  DateTimePicker,
  DateTimePickerProps,
  PickersActionBarAction
} from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { useMemo } from 'react';

export type DatePickerType = 'date' | 'datetime';

export interface DatePickerComponentProps 
  extends Omit<
    DatePickerProps<Dayjs> & DateTimePickerProps<Dayjs>, 
    'value' | 'onChange' | 'renderInput' | 'disabled' | 'format'
  > {
  label: string;
  value: Dayjs | null;
  onChange: (date: Dayjs | null) => void;
  errorMessage?: string;
  isOptional?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  type?: DatePickerType;
  format?: {
    date?: string;
    datetime?: string;
  };
  ampm?: boolean;
  isLoading?: boolean;
}

export const DatePickerComponent = ({
  label,
  value,
  onChange,
  errorMessage,
  isOptional,
  disablePast = false,
  disableFuture = false,
  type = 'date',
  format = { date: 'DD/MM/YYYY', datetime: 'DD/MM/YYYY HH:mm' },
  ampm = false,
  isLoading = false,
  ...props
}: DatePickerComponentProps) => {
  
  const displayLabel = isOptional ? `${label} (Opcional)` : label;

  const commonSlotProps = useMemo(() => ({
    textField: {
      error: !!errorMessage,
      helperText: errorMessage,
      fullWidth: true,
      sx: { mb: 3 }
    },
    actionBar: {
      actions: ['clear', 'today'] as PickersActionBarAction[], 
    },
    openPickerButton: { color: 'primary' as const },
    previousIconButton: { color: 'primary' as const },
    nextIconButton: { color: 'primary' as const },
    switchViewButton: { color: 'primary' as const },
  }), [errorMessage]);

  if (type === 'datetime') {
    return (
      <DateTimePicker
        {...props}
        label={displayLabel}
        value={value}
        onChange={onChange}
        disabled={isLoading}
        disablePast={disablePast}
        disableFuture={disableFuture}
        format={format.datetime}
        ampm={ampm}
        slotProps={commonSlotProps}
        timeSteps={{ minutes: 1 }}
      />
    );
  }

  return (
    <DatePicker
      {...props}
      label={displayLabel}
      value={value}
      onChange={onChange}
      disabled={isLoading}
      disablePast={disablePast}
      disableFuture={disableFuture}
      format={format.date}
      slotProps={commonSlotProps}
    />
  );
};

export default DatePickerComponent;